import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { CardSkeleton } from '../../Components/common/Skeleton';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  ShieldCheck,
  Activity,
  FileText,
  CreditCard,
  Globe,
  Download,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react';
import HospitalEditModal from '../../Components/hospitals/HospitalEditModal';

const HospitalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState('general');
  const [verificationStatus, setVerificationStatus] = useState('verified');
  const [rejectionReason, setRejectionReason] = useState('');
  const [surgeries, setSurgeries] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'customer_care' && user.role !== 'cc_head') {
      toast.error('Access Denied: Admin or CC/CC Head role required');
      navigate('/dashboard');
      return;
    }
    fetchHospital();
    fetchSurgeries();
  }, [id, user]);

  const fetchSurgeries = async () => {
    try {
      const response = await api.get('/surgeries', { params: { hospitalId: id, limit: 100 } });
      setSurgeries(response.data.data.surgeries);
    } catch (error) {
      console.error('Failed to fetch surgeries for hospital', error);
    }
  };

  const fetchHospital = async () => {
    try {
      const response = await api.get(`/hospitals/${id}`);
      setHospital(response.data.data);
    } catch (error) {
      toast.error('Failed to load hospital');
      navigate('/hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setUpdating(true);
    try {
      await api.put(`/hospitals/${id}/verify`, {
        status: verificationStatus,
        rejectionReason: verificationStatus === 'rejected' ? rejectionReason : undefined
      });
      toast.success(`Hospital ${verificationStatus} successfully!`);
      setShowVerifyModal(false);
      setVerificationStatus('verified');
      setRejectionReason('');
      fetchHospital();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify hospital');
    } finally {
      setUpdating(false);
    }
  };

  const getVerificationColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-slate-200 animate-pulse rounded-lg"></div>
            <div className="h-8 w-48 bg-slate-200 animate-pulse rounded-lg"></div>
          </div>
          <div className="h-10 w-32 bg-slate-100 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
              <div className="h-6 w-1/3 bg-slate-100 rounded animate-pulse"></div>
              <div className="grid grid-cols-2 gap-8">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-3 w-20 bg-slate-50 rounded animate-pulse"></div>
                    <div className="h-5 w-full bg-slate-100 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  const now = new Date();
  const upcomingSurgeries = (surgeries || []).filter(s => {
    const scheduled = new Date(s.scheduledDate);
    return scheduled >= now && s.status !== 'cancelled' && s.status !== 'completed';
  }).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

  const previousSurgeries = (surgeries || []).filter(s => {
    const scheduled = new Date(s.scheduledDate);
    return scheduled < now || s.status === 'cancelled' || s.status === 'completed';
  }).sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

  const totalRevenue = (surgeries || [])
    .filter(s => s.status !== 'cancelled')
    .reduce((sum, s) => sum + (s.surgeryCost || 0), 0);

  if (!hospital) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Hospital not found</p>
        <Link to="/hospitals" className="text-primary-600 hover:text-primary-700">
          Back to hospitals
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Hospital Details</h1>
        </div>
        <div className="flex space-x-2">
          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') &&
            hospital.verificationStatus === 'pending' && (
              <button
                onClick={() => setShowVerifyModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Verify Hospital
              </button>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6 border-b border-slate-50 pb-4">
              <div className="flex items-center gap-3">
                {hospital.documents?.find(d => d.type === 'PHOTO') ? (
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 flex-shrink-0">
                    <img
                      src={hospital.documents.find(d => d.type === 'PHOTO').url}
                      alt="Hospital Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="p-3 bg-indigo-50 rounded-2xl flex-shrink-0">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Hospital Information</h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Core Entity Details</p>
                </div>
              </div>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('general');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all flex items-center gap-1 shadow-sm"
                >
                  Edit Info
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Hospital Name</p>
                <p className="text-sm font-bold text-slate-700">{hospital.hospitalName}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Registration Number</p>
                <p className="text-sm font-bold text-slate-700">{hospital.registrationNumber}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Type</p>
                <p className="text-sm font-bold text-slate-700 capitalize">
                  {hospital.hospitalType === 'Others' && hospital.otherHospitalType ? `${hospital.hospitalType} - ${hospital.otherHospitalType}` : hospital.hospitalType}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Issuing Authority</p>
                <p className="text-sm font-bold text-slate-700">{hospital.issuingAuthority || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">License Validity</p>
                <p className="text-sm font-bold text-slate-700">{hospital.licenseValidity ? new Date(hospital.licenseValidity).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Entity Contact Name</p>
                <p className="text-sm font-bold text-slate-700">{hospital.entityContactName || hospital.hospitalPartners?.[0]?.name || hospital.authorizedSignatories?.[0]?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Entity Contact Phone</p>
                <p className="text-sm font-bold text-slate-700">{hospital.entityContactPhone || hospital.hospitalPartners?.[0]?.phone || hospital.authorizedSignatories?.[0]?.phone || 'N/A'}</p>
              </div>
              <div className="col-span-2 sm:col-span-3 pt-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Hospital Partners</p>
                {hospital.hospitalPartners?.length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {hospital.hospitalPartners.map((p, idx) => p.name ? (
                      <div key={idx} className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <p className="text-sm font-bold text-slate-700 capitalize">{p.name.toLowerCase()}</p>
                        {(p.email || p.phone) && (
                          <div className="flex flex-col mt-1">
                            {p.phone && <span className="text-[10px] text-slate-500 font-medium">📞 {p.phone}</span>}
                            {p.email && <span className="text-[10px] text-slate-500 font-medium truncate" title={p.email}>✉️ {p.email}</span>}
                          </div>
                        )}
                      </div>
                    ) : null)}
                  </div>
                ) : (
                  <p className="text-sm font-bold text-slate-700">N/A</p>
                )}
              </div>

              {hospital.authorizedSignatories && hospital.authorizedSignatories.length > 0 && (
                <div className="col-span-2 sm:col-span-3">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Authorized Signatories</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {hospital.authorizedSignatories.map((s, idx) => s.name ? (
                      <div key={idx} className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <p className="text-sm font-bold text-slate-700 capitalize">{s.name.toLowerCase()}</p>
                        {s.designation && <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest mt-0.5 mb-1">{s.designation}</p>}
                        {(s.email || s.phone) && (
                          <div className="flex flex-col mt-1">
                            {s.phone && <span className="text-[10px] text-slate-500 font-medium">📞 {s.phone}</span>}
                            {s.email && <span className="text-[10px] text-slate-500 font-medium truncate" title={s.email}>✉️ {s.email}</span>}
                          </div>
                        )}
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 pt-8 border-t border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Activity className="w-3 h-3" /> Infrastructure & Capacity
                </p>
                {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                  <button
                    onClick={() => {
                      setEditModalTab('compliance');
                      setShowEditModal(true);
                    }}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                  >
                    Edit Capacity
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Beds</p>
                  <p className="text-sm font-black text-primary-600">{hospital.totalBeds || '0'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">ICU Beds</p>
                  <p className="text-sm font-black text-indigo-600">{hospital.icuBeds || '0'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">OT Rooms</p>
                  <p className="text-sm font-black text-emerald-600">{hospital.otRooms || '0'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Emergency</p>
                  <p className="text-sm font-black text-rose-600">{hospital.emergencyServices ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>

            {hospital.description && (
              <div className="mt-6 p-4 bg-amber-50/30 rounded-xl border border-amber-100/50">
                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Description</p>
                <p className="text-xs font-medium text-slate-600 leading-relaxed">{hospital.description}</p>
              </div>
            )}
          </div>

          {hospital.address && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Address</h2>
                {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                  <button
                    onClick={() => {
                      setEditModalTab('address');
                      setShowEditModal(true);
                    }}
                    className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                  >
                    Edit Location
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Street</p>
                  <p className="font-medium">{hospital.address.street}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <p className="font-medium">{hospital.address.city}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">State</p>
                  <p className="font-medium">{hospital.address.state}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pincode</p>
                  <p className="font-medium">{hospital.address.pincode}</p>
                </div>
              </div>
            </div>
          )}

          {hospital.specialties && hospital.specialties.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Specialties</h2>
              <div className="flex flex-wrap gap-2">
                {hospital.specialties.map((specialty) => (
                  <span
                    key={specialty}
                    className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {hospital.facilities && hospital.facilities.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Facilities</h2>
              <div className="flex flex-wrap gap-2">
                {hospital.facilities.map((facility) => (
                  <span
                    key={facility}
                    className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                  >
                    {facility}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Clinical Registration Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-indigo-50 rounded-2xl">
                <ShieldCheck className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Clinical Registration</h2>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Medical Licensing & Credentials</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Registration Number</p>
                <p className="text-sm font-bold text-slate-700">{hospital.registrationNumber || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Issuing Authority</p>
                <p className="text-sm font-bold text-slate-700">{hospital.issuingAuthority || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">License Validity</p>
                <p className="text-sm font-bold text-slate-700">
                  {hospital.licenseValidity ? new Date(hospital.licenseValidity).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Registration Certificate</p>
                {hospital.documents?.find(d => d.type === 'HOSPITAL_REGISTRATION' || d.type === 'REGISTRATION_CERTIFICATE' || d.type === 'Registration Certificate') ? (
                  <a
                    href={hospital.documents.find(d => d.type === 'HOSPITAL_REGISTRATION' || d.type === 'REGISTRATION_CERTIFICATE' || d.type === 'Registration Certificate').url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold w-fit"
                  >
                    <Download className="w-4 h-4" /> View Certificate
                  </a>
                ) : (
                  <p className="text-xs text-red-500 font-bold italic">No Registration Certificate Uploaded</p>
                )}
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Signed MoU Agreement</p>
                {(hospital.documents?.find(d => d.type === 'SIGNED_AGREEMENT') || hospital.user?.onboardingDocuments?.find(d => d.type === 'SIGNED_AGREEMENT')) ? (
                  <a
                    href={(hospital.documents?.find(d => d.type === 'SIGNED_AGREEMENT') || hospital.user?.onboardingDocuments?.find(d => d.type === 'SIGNED_AGREEMENT')).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-xl hover:bg-indigo-100 transition-colors text-xs font-bold w-fit"
                  >
                    <Download className="w-4 h-4" /> View Agreement
                  </a>
                ) : (
                  <p className="text-xs text-slate-400 font-bold italic">Not Signed Yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Digital Presence Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-50 rounded-2xl">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Digital Presence (Optional)</h2>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Web & Mapping Integrations</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Website URL</p>
                {hospital.websiteUrl || hospital.website ? (
                  <a
                    href={hospital.websiteUrl || hospital.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-bold text-primary-600 hover:underline inline-flex items-center gap-1"
                  >
                    {hospital.websiteUrl || hospital.website} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <p className="text-sm font-bold text-slate-400 italic">Not Provided</p>
                )}
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Google Maps Location</p>
                {hospital.address?.googleMapLocation ? (
                  <a
                    href={hospital.address.googleMapLocation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8F0FE] text-[#1A73E8] border border-[#D2E3FC] rounded-xl hover:bg-[#D2E3FC] transition-colors text-xs font-bold w-fit"
                  >
                    <MapPin className="w-4 h-4 text-[#1A73E8]" /> Open Google Maps
                  </a>
                ) : (
                  <p className="text-sm font-bold text-slate-400 italic">Not Provided</p>
                )}
              </div>
            </div>
          </div>

          {/* Surgeries Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-black text-[#143E78] uppercase tracking-tight mb-6">Upcoming Surgeries</h2>
            {upcomingSurgeries.length > 0 ? (
              <div className="space-y-4 mb-8">
                {upcomingSurgeries.map(surgery => (
                  <Link key={surgery._id} to={`/surgeries/${surgery._id}`} className="flex justify-between items-center p-4 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors">
                    <div>
                      <p className="font-bold text-[#143E78]">{surgery.surgeryName} <span className="text-xs font-normal text-gray-500">({surgery.surgeryType})</span></p>
                      <p className="text-xs text-gray-600 mt-1">Patient: {surgery.lead?.patientName || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-800">{new Date(surgery.scheduledDate).toLocaleDateString('en-GB')}</p>
                      <p className="text-xs text-gray-500">{surgery.scheduledTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic mb-8">No upcoming surgeries scheduled.</p>
            )}

            <h2 className="text-xl font-black text-[#143E78] uppercase tracking-tight mb-6">Previous Surgeries</h2>
            {previousSurgeries.length > 0 ? (
              <div className="space-y-4">
                {previousSurgeries.map(surgery => (
                  <Link key={surgery._id} to={`/surgeries/${surgery._id}`} className="flex justify-between items-center p-4 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-colors">
                    <div>
                      <p className="font-bold text-gray-700">{surgery.surgeryName}</p>
                      <p className="text-xs text-gray-500 mt-1">Status: <span className="uppercase">{surgery.status.replace(/_/g, ' ')}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-600">{new Date(surgery.scheduledDate).toLocaleDateString('en-GB')}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No previous surgeries recorded.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Phone className="w-5 h-5 text-indigo-600" />
                Contact Information
              </h2>
              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
                <button
                  onClick={() => {
                    setEditModalTab('contact');
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
                >
                  Edit Contacts
                </button>
              )}
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                  <User className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Person</p>
                  <p className="text-sm font-bold text-slate-700">{hospital.contactPerson?.name}</p>
                </div>
              </div>

              {hospital.contactPerson?.designation && (
                <div className="flex items-start gap-3 ml-11">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Designation</p>
                    <p className="text-sm font-bold text-slate-700">{hospital.contactPerson.designation}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Number</p>
                  <p className="text-sm font-bold text-slate-700">{hospital.contactPerson?.phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</p>
                  <p className="text-sm font-bold text-slate-700">{hospital.contactPerson?.email || hospital.mailId || 'N/A'}</p>
                </div>
              </div>

              {hospital.websiteUrl && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Website</p>
                    <a href={hospital.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-primary-600 hover:underline flex items-center gap-1">
                      {hospital.websiteUrl} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}

              {hospital.designatedEmployee?.name && (
                <>
                  <div className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-100">
                    <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Designated Employee</p>
                      <p className="text-sm font-bold text-slate-700">{hospital.designatedEmployee.name}</p>
                    </div>
                  </div>

                  {hospital.designatedEmployee.role && (
                    <div className="flex items-start gap-3 ml-11">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Role</p>
                        <p className="text-sm font-bold text-slate-700">{hospital.designatedEmployee.role}</p>
                      </div>
                    </div>
                  )}

                  {hospital.designatedEmployee.phone && (
                    <div className="flex items-start gap-3 ml-11">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone</p>
                        <p className="text-sm font-bold text-slate-700">{hospital.designatedEmployee.phone}</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {hospital.alternativeEmails && hospital.alternativeEmails.length > 0 && (
                <div className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-100">
                  <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Alternative Emails</p>
                    <p className="text-sm font-bold text-slate-700">
                      {hospital.alternativeEmails.filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Verification Status
            </h2>
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <span className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-full ${getVerificationColor(hospital.verificationStatus)}`}>
                {hospital.verificationStatus}
              </span>
              {hospital.verifiedBy && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">CC (Verified By)</p>
                  <p className="text-sm font-bold text-indigo-600">{hospital.verifiedBy.firstName} {hospital.verifiedBy.lastName}</p>
                </div>
              )}
              {hospital.user?.invitedBy && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">CC (Invited By)</p>
                  <p className="text-sm font-bold text-slate-600">{hospital.user.invitedBy.firstName} {hospital.user.invitedBy.lastName}</p>
                </div>
              )}
              {hospital.rejectionReason && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Rejection Reason</p>
                  <p className="text-sm font-bold text-red-600">{hospital.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              Performance Statistics
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Surgeries</p>
                <p className="text-xl font-black text-slate-700">{surgeries.length}</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Total Revenue</p>
                <p className="text-xl font-black text-indigo-600">₹{totalRevenue.toLocaleString()}</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Total Patients</p>
                <p className="text-xl font-black text-emerald-700">{hospital.totalPatients || 0}</p>
              </div>
            </div>
          </div>

          {hospital.insuranceAccepted && hospital.insuranceAccepted.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Insurance Accepted</h2>
              <div className="flex flex-wrap gap-2">
                {hospital.insuranceAccepted.map((insurance) => (
                  <span
                    key={insurance}
                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {insurance}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Financial & Documents Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Documents */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600" />
              Hospital Documents
            </h2>
            <span className="text-xs font-medium text-gray-400 uppercase tracking-widest">
              {(hospital.documents?.length || 0) + (hospital.user?.onboardingDocuments?.length || 0)} Files
            </span>
          </div>
          <div className="space-y-4">
            {((hospital.documents || []).concat(hospital.user?.onboardingDocuments || [])).length > 0 ? (
              ((hospital.documents || []).concat(hospital.user?.onboardingDocuments || [])).map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-primary-200 transition-all group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-primary-600 shadow-sm border border-slate-50">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-700">{doc.name}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{doc.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                      title="View Document"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">No documents uploaded</p>
              </div>
            )}
          </div>
        </div>

        {/* Financial Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              Financial & KYC Details
            </h2>
            {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
              <button
                onClick={() => {
                  setEditModalTab('finance');
                  setShowEditModal(true);
                }}
                className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[10px] font-black uppercase tracking-wider rounded-xl transition-all shadow-sm"
              >
                Edit Financials
              </button>
            )}
          </div>

          <div className="space-y-6">
            {/* KYC IDs */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">PAN Number</p>
                <p className="text-sm font-bold text-slate-700">{hospital.panNumber || 'Not Provided'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">GST Number</p>
                <p className="text-sm font-bold text-slate-700">{hospital.gstNumber || 'Not Provided'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">GST Billing Name</p>
                <p className="text-sm font-bold text-slate-700">{hospital.gstBillingName || 'Not Provided'}</p>
              </div>
            </div>

            {/* Custom Payment Configuration */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-4 bg-orange-50/50 rounded-xl border border-orange-100/50 flex items-center justify-between">
                <p className="text-[10px] font-black text-orange-600 uppercase tracking-widest mb-1">Platform Fee %</p>
                <p className="text-sm font-bold text-orange-700">{hospital.platformFeePercent || 0}%</p>
              </div>
              <div className="p-4 bg-orange-50/50 rounded-xl border border-orange-100/50 flex items-center justify-between">
                <p className="text-[10px] font-black text-orange-600 uppercase tracking-widest mb-1">GST %</p>
                <p className="text-sm font-bold text-orange-700">{hospital.gstPercent || 0}%</p>
              </div>
            </div>

            {/* Bank Details */}
            {hospital.bankDetails ? (
              <div className="p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100/50">
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Building2 className="w-3 h-3" /> Bank Account Information
                </p>
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Account Name</p>
                    <p className="text-xs font-bold text-slate-700">{hospital.bankDetails.accountName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Account Number</p>
                    <p className="text-xs font-bold text-slate-700">{hospital.bankDetails.accountNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Bank Name</p>
                    <p className="text-xs font-bold text-slate-700">{hospital.bankDetails.bankName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">IFSC Code</p>
                    <p className="text-xs font-bold text-slate-700">{hospital.bankDetails.ifscCode || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-medium">Bank details not provided</p>
              </div>
            )}

            {/* Additional Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">NABH Accredited</p>
                <div className="flex items-center gap-2">
                  {hospital.nabhAccredited ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-300" />
                  )}
                  <p className="text-sm font-bold text-slate-700">{hospital.nabhAccredited ? `Yes${hospital.nabhNumber ? ` (${hospital.nabhNumber})` : ''}` : 'No'}</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Ayushman Bharat</p>
                <div className="flex items-center gap-2">
                  {hospital.ayushmanBharatEmpanelled ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-300" />
                  )}
                  <p className="text-sm font-bold text-slate-700">{hospital.ayushmanBharatEmpanelled ? `Yes${hospital.ayushmanNumber ? ` (${hospital.ayushmanNumber})` : ''}` : 'No'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Verify Hospital</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={verificationStatus}
                  onChange={(e) => setVerificationStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {verificationStatus === 'rejected' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowVerifyModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVerify}
                disabled={updating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updating ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <HospitalEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        hospital={hospital}
        onSuccess={fetchHospital}
        defaultTab={editModalTab}
      />
    </div>
  );
};

export default HospitalDetail;
